import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { 
  Wallet, Plus, Eye, Edit, Trash2, DollarSign, Calendar, User, 
  CheckCircle, Clock, Package, ArrowLeft, FileText,
  AlertCircle, Check, Building, UserCircle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const CATEGORIAS = [
  { value: "Materiales", label: "Materiales" },
  { value: "Mano de obra", label: "Mano de obra" },
  { value: "Servicios", label: "Servicios" },
  { value: "Limpieza", label: "Limpieza" },
  { value: "Reparaciones", label: "Reparaciones" },
  { value: "Otros", label: "Otros" },
];

const TIPOS_LIGADO = [
  { value: "Propiedad", label: "Propiedad", icon: Building },
  { value: "Persona", label: "Persona/Inquilino", icon: UserCircle },
  { value: "Oficina", label: "Gasto de Oficina", icon: Building },
  { value: "Ninguno", label: "Gasto General", icon: Package },
];

export default function CajasChicas({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState("cajas");
  const [loading, setLoading] = useState(false);
  
  // Cajas Chicas state
  const [cajas, setCajas] = useState([]);
  const [showCajaModal, setShowCajaModal] = useState(false);
  const [editingCaja, setEditingCaja] = useState(null);
  const [cajaForm, setCajaForm] = useState({
    fecha_entrega: new Date().toISOString().split("T")[0],
    monto_entregado: "",
    responsable: "",
    origen: "",
    descripcion: ""
  });
  
  // Detail view state
  const [selectedCaja, setSelectedCaja] = useState(null);
  const [showGastoModal, setShowGastoModal] = useState(false);
  const [gastoForm, setGastoForm] = useState({
    tipo_ligado: "Propiedad",
    contrato_id: "",
    persona_nombre: "",
    descripcion: "",
    monto: "",
    fecha_gasto: new Date().toISOString().split("T")[0],
    categoria: "Otros"
  });
  
  // Contracts for dropdown
  const [contratos, setContratos] = useState([]);
  
  // Gastos por Propiedad state
  const [propiedadesConGastos, setPropiedadesConGastos] = useState([]);
  const [selectedPropiedad, setSelectedPropiedad] = useState(null);
  const [gastosPropiedad, setGastosPropiedad] = useState(null);
  const [showCobrarModal, setShowCobrarModal] = useState(false);
  const [selectedGastos, setSelectedGastos] = useState([]);
  const [comentarioCobro, setComentarioCobro] = useState("");

  // Otros Gastos state
  const [otrosGastosPorCategoria, setOtrosGastosPorCategoria] = useState([]);
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [gastosCategoria, setGastosCategoria] = useState(null);

  useEffect(() => {
    fetchContratos();
    if (activeTab === "cajas") {
      fetchCajas();
    } else if (activeTab === "propiedades") {
      fetchPropiedadesConGastos();
    } else if (activeTab === "otros") {
      fetchOtrosGastos();
    }
  }, [activeTab]);

  const fetchContratos = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/contracts?estatus=Activo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContratos(response.data || []);
    } catch (error) {
      console.error("Error fetching contratos:", error);
    }
  };

  const fetchCajas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/cajas-chicas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCajas(response.data || []);
    } catch (error) {
      toast.error("Error al cargar cajas chicas");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCajaDetail = async (cajaId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/cajas-chicas/${cajaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCaja(response.data);
    } catch (error) {
      toast.error("Error al cargar detalle de caja");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPropiedadesConGastos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/cajas-chicas/gastos-por-propiedad?solo_pendientes=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPropiedadesConGastos(response.data || []);
    } catch (error) {
      toast.error("Error al cargar gastos por propiedad");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOtrosGastos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/cajas-chicas/otros-gastos?solo_pendientes=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOtrosGastosPorCategoria(response.data || []);
    } catch (error) {
      toast.error("Error al cargar otros gastos");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGastosCategoria = async (categoria) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/cajas-chicas/otros-gastos/${encodeURIComponent(categoria)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGastosCategoria(response.data);
      setSelectedCategoria(categoria);
    } catch (error) {
      toast.error("Error al cargar gastos de categoría");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGastosPropiedad = async (contratoId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/cajas-chicas/gastos-por-propiedad/${contratoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGastosPropiedad(response.data);
      setSelectedPropiedad(contratoId);
    } catch (error) {
      toast.error("Error al cargar gastos de propiedad");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCaja = async () => {
    if (!cajaForm.monto_entregado || !cajaForm.responsable) {
      toast.error("Por favor complete los campos requeridos");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingCaja) {
        await axios.put(`${API}/cajas-chicas/${editingCaja.id}`, cajaForm, { headers });
        toast.success("Caja chica actualizada");
      } else {
        await axios.post(`${API}/cajas-chicas`, {
          ...cajaForm,
          monto_entregado: parseFloat(cajaForm.monto_entregado)
        }, { headers });
        toast.success("Caja chica creada");
      }
      
      setShowCajaModal(false);
      setEditingCaja(null);
      resetCajaForm();
      fetchCajas();
    } catch (error) {
      toast.error("Error al guardar caja chica");
      console.error(error);
    }
  };

  const resetCajaForm = () => {
    setCajaForm({
      fecha_entrega: new Date().toISOString().split("T")[0],
      monto_entregado: "",
      responsable: "",
      origen: "",
      descripcion: ""
    });
  };

  const handleEditCaja = (caja) => {
    setEditingCaja(caja);
    setCajaForm({
      fecha_entrega: caja.fecha_entrega,
      monto_entregado: caja.monto_entregado,
      responsable: caja.responsable,
      origen: caja.origen || "",
      descripcion: caja.descripcion || ""
    });
    setShowCajaModal(true);
  };

  const handleDeleteCaja = async (cajaId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta caja chica y todos sus gastos?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/cajas-chicas/${cajaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Caja chica eliminada");
      fetchCajas();
    } catch (error) {
      toast.error("Error al eliminar caja chica");
      console.error(error);
    }
  };

  const handleCloseCaja = async (cajaId) => {
    if (!window.confirm("¿Estás seguro de cerrar esta caja chica? Ya no se podrán agregar gastos.")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/cajas-chicas/${cajaId}`, { estatus: "Cerrada" }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Caja chica cerrada");
      fetchCajas();
      if (selectedCaja?.id === cajaId) {
        fetchCajaDetail(cajaId);
      }
    } catch (error) {
      toast.error("Error al cerrar caja chica");
      console.error(error);
    }
  };

  const handleSaveGasto = async () => {
    if (!gastoForm.descripcion || !gastoForm.monto) {
      toast.error("Por favor complete los campos requeridos");
      return;
    }

    // Validar según tipo de ligado
    if (gastoForm.tipo_ligado === "Propiedad" && !gastoForm.contrato_id) {
      toast.error("Seleccione una propiedad");
      return;
    }
    if (gastoForm.tipo_ligado === "Persona" && !gastoForm.persona_nombre) {
      toast.error("Ingrese el nombre de la persona");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/cajas-chicas/${selectedCaja.id}/gastos`, {
        ...gastoForm,
        caja_chica_id: selectedCaja.id,
        monto: parseFloat(gastoForm.monto),
        contrato_id: gastoForm.tipo_ligado === "Propiedad" ? gastoForm.contrato_id : null,
        persona_nombre: gastoForm.tipo_ligado === "Persona" ? gastoForm.persona_nombre : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Gasto agregado");
      setShowGastoModal(false);
      resetGastoForm();
      fetchCajaDetail(selectedCaja.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar gasto");
      console.error(error);
    }
  };

  const resetGastoForm = () => {
    setGastoForm({
      tipo_ligado: "Propiedad",
      contrato_id: "",
      persona_nombre: "",
      descripcion: "",
      monto: "",
      fecha_gasto: new Date().toISOString().split("T")[0],
      categoria: "Otros"
    });
  };

  const handleDeleteGasto = async (gastoId) => {
    if (!window.confirm("¿Estás seguro de eliminar este gasto?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/cajas-chicas/gastos/${gastoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Gasto eliminado");
      fetchCajaDetail(selectedCaja.id);
    } catch (error) {
      toast.error("Error al eliminar gasto");
      console.error(error);
    }
  };

  const handleMarcarCobrados = async () => {
    if (selectedGastos.length === 0) {
      toast.error("Selecciona al menos un gasto");
      return;
    }
    if (!comentarioCobro.trim()) {
      toast.error("Ingresa el comentario del cobro (mes y corte)");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/cajas-chicas/marcar-cobrados`, {
        gasto_ids: selectedGastos,
        comentario_cobro: comentarioCobro
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setShowCobrarModal(false);
      setSelectedGastos([]);
      setComentarioCobro("");
      
      // Refresh based on active tab
      if (activeTab === "propiedades" && selectedPropiedad) {
        fetchGastosPropiedad(selectedPropiedad);
        fetchPropiedadesConGastos();
      } else if (activeTab === "otros" && selectedCategoria) {
        fetchGastosCategoria(selectedCategoria);
        fetchOtrosGastos();
      }
    } catch (error) {
      toast.error("Error al marcar gastos como cobrados");
      console.error(error);
    }
  };

  const toggleGastoSelection = (gastoId) => {
    setSelectedGastos(prev => 
      prev.includes(gastoId) 
        ? prev.filter(id => id !== gastoId)
        : [...prev, gastoId]
    );
  };

  const selectAllPendientes = () => {
    if (gastosPropiedad) {
      const pendientes = gastosPropiedad.gastos.filter(g => !g.cobrado).map(g => g.id);
      setSelectedGastos(pendientes);
    }
  };

  const getGastoLigadoDisplay = (gasto) => {
    if (gasto.tipo_ligado === "Propiedad" && gasto.inmueble_nombre) {
      return gasto.inmueble_nombre;
    }
    if (gasto.tipo_ligado === "Persona" && gasto.persona_nombre) {
      return gasto.persona_nombre;
    }
    if (gasto.tipo_ligado === "Oficina") {
      return "Gasto de Oficina";
    }
    return "Gasto General";
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto space-y-6 fade-in">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Control de Cajas Chicas</h1>
            <p className="text-gray-600 mt-1">Administra los gastos de mantenimiento</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex gap-4 overflow-x-auto">
            <button
              onClick={() => { setActiveTab("cajas"); setSelectedCaja(null); setSelectedPropiedad(null); }}
              data-testid="tab-cajas"
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                activeTab === "cajas"
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Wallet className="w-4 h-4 inline mr-2" />
              Cajas Chicas
            </button>
            <button
              onClick={() => { setActiveTab("propiedades"); setSelectedCaja(null); setSelectedPropiedad(null); setSelectedCategoria(null); }}
              data-testid="tab-propiedades"
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                activeTab === "propiedades"
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Package className="w-4 h-4 inline mr-2" />
              Gastos por Propiedad
            </button>
            <button
              onClick={() => { setActiveTab("otros"); setSelectedCaja(null); setSelectedPropiedad(null); setSelectedCategoria(null); }}
              data-testid="tab-otros"
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                activeTab === "otros"
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Otros Gastos
            </button>
          </div>
        </div>

        {/* Tab Content: Cajas Chicas - LISTA */}
        {activeTab === "cajas" && !selectedCaja && (
          <>
            <div className="flex justify-end">
              <Button onClick={() => { resetCajaForm(); setEditingCaja(null); setShowCajaModal(true); }} data-testid="btn-nueva-caja">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Caja Chica
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Lista de Cajas Chicas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : cajas.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Wallet className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p>No hay cajas chicas registradas</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Fecha</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Responsable</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Origen</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Entregado</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Gastado</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Saldo</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Gastos</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Estado</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cajas.map((caja, index) => (
                          <tr key={caja.id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                            <td className="py-3 px-4 text-sm">{formatDate(caja.fecha_entrega)}</td>
                            <td className="py-3 px-4 text-sm font-medium">{caja.responsable}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">{caja.origen || "-"}</td>
                            <td className="py-3 px-4 text-sm text-right font-medium">{formatCurrency(caja.monto_entregado)}</td>
                            <td className="py-3 px-4 text-sm text-right text-red-600">{formatCurrency(caja.monto_gastado)}</td>
                            <td className={`py-3 px-4 text-sm text-right font-bold ${caja.saldo_disponible >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(caja.saldo_disponible)}
                            </td>
                            <td className="py-3 px-4 text-sm text-center">{caja.total_gastos}</td>
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                caja.estatus === "Abierta" 
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}>
                                {caja.estatus}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => fetchCajaDetail(caja.id)} title="Ver detalle">
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleEditCaja(caja)} title="Editar">
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {caja.estatus === "Abierta" && (
                                  <Button variant="ghost" size="sm" onClick={() => handleCloseCaja(caja.id)} title="Cerrar caja">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteCaja(caja.id)} title="Eliminar" className="text-red-600 hover:text-red-700">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Detalle de Caja Chica */}
        {activeTab === "cajas" && selectedCaja && (
          <>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => setSelectedCaja(null)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div className="flex-1">
                <h2 className="text-xl font-bold">Caja del {formatDate(selectedCaja.fecha_entrega)}</h2>
                <p className="text-gray-600">Responsable: {selectedCaja.responsable} {selectedCaja.origen && `| Origen: ${selectedCaja.origen}`}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                selectedCaja.estatus === "Abierta" 
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}>
                {selectedCaja.estatus}
              </span>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Monto Entregado</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(selectedCaja.monto_entregado)}</p>
                    </div>
                    <Wallet className="w-8 h-8 text-indigo-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Monto Gastado</p>
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(selectedCaja.monto_gastado)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Saldo Disponible</p>
                      <p className={`text-2xl font-bold ${selectedCaja.saldo_disponible >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(selectedCaja.saldo_disponible)}
                      </p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Gastos</p>
                      <p className="text-2xl font-bold text-gray-900">{selectedCaja.gastos?.length || 0}</p>
                    </div>
                    <FileText className="w-8 h-8 text-gray-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Gastos List */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Gastos Registrados</CardTitle>
                  {selectedCaja.estatus === "Abierta" && (
                    <Button onClick={() => { resetGastoForm(); setShowGastoModal(true); }} data-testid="btn-agregar-gasto">
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Gasto
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!selectedCaja.gastos || selectedCaja.gastos.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p>No hay gastos registrados en esta caja</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Fecha</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Tipo</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Ligado a</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Categoría</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Descripción</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Monto</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Cobrado</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCaja.gastos.map((gasto, index) => (
                          <tr key={gasto.id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                            <td className="py-3 px-4 text-sm">{formatDate(gasto.fecha_gasto)}</td>
                            <td className="py-3 px-4 text-sm">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                                gasto.tipo_ligado === "Propiedad" ? "bg-blue-100 text-blue-800" :
                                gasto.tipo_ligado === "Persona" ? "bg-purple-100 text-purple-800" :
                                gasto.tipo_ligado === "Oficina" ? "bg-yellow-100 text-yellow-800" :
                                "bg-gray-100 text-gray-800"
                              }`}>
                                {gasto.tipo_ligado || "Propiedad"}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm font-medium">{getGastoLigadoDisplay(gasto)}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">{gasto.categoria}</td>
                            <td className="py-3 px-4 text-sm">{gasto.descripcion}</td>
                            <td className="py-3 px-4 text-sm text-right font-medium">{formatCurrency(gasto.monto)}</td>
                            <td className="py-3 px-4 text-center">
                              {gasto.cobrado ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                  <Check className="w-3 h-3 mr-1" />
                                  Sí
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                                  <Clock className="w-3 h-3 mr-1" />
                                  No
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteGasto(gasto.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Tab Content: Gastos por Propiedad - LISTA */}
        {activeTab === "propiedades" && !selectedPropiedad && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Gastos por Propiedad
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : propiedadesConGastos.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p>No hay propiedades con gastos registrados</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Propiedad</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Propietario</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Total Gastos</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Monto Total</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Pendientes</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Monto Pendiente</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Estado</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {propiedadesConGastos.map((prop, index) => (
                        <tr key={prop.contrato_id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                          <td className="py-3 px-4 text-sm font-medium">{prop.inmueble_nombre}</td>
                          <td className="py-3 px-4 text-sm text-gray-600">{prop.propietario_nombre}</td>
                          <td className="py-3 px-4 text-sm text-center">{prop.total_gastos}</td>
                          <td className="py-3 px-4 text-sm text-right font-medium">{formatCurrency(prop.monto_total)}</td>
                          <td className="py-3 px-4 text-sm text-center">{prop.gastos_pendientes}</td>
                          <td className="py-3 px-4 text-sm text-right font-medium text-orange-600">{formatCurrency(prop.monto_pendiente)}</td>
                          <td className="py-3 px-4 text-center">
                            {prop.gastos_pendientes > 0 ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Pendiente
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Cobrado
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Button variant="ghost" size="sm" onClick={() => fetchGastosPropiedad(prop.contrato_id)} title="Ver detalle">
                              <Eye className="w-4 h-4 mr-1" />
                              Ver
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Detalle de Gastos por Propiedad */}
        {activeTab === "propiedades" && selectedPropiedad && gastosPropiedad && (
          <>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => { setSelectedPropiedad(null); setGastosPropiedad(null); setSelectedGastos([]); }}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div className="flex-1">
                <h2 className="text-xl font-bold">{gastosPropiedad.inmueble_nombre}</h2>
                <p className="text-gray-600">Propietario: {gastosPropiedad.propietario_nombre}</p>
              </div>
              {gastosPropiedad.totales.pendientes > 0 && (
                <Button onClick={() => setShowCobrarModal(true)} data-testid="btn-cobrar">
                  <Check className="w-4 h-4 mr-2" />
                  Marcar como Cobrados ({selectedGastos.length || "Seleccionar"})
                </Button>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500">Total Gastos</p>
                  <p className="text-2xl font-bold">{gastosPropiedad.totales.cantidad}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500">Monto Total</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(gastosPropiedad.totales.monto_total)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500">Pendientes</p>
                  <p className="text-2xl font-bold text-orange-600">{gastosPropiedad.totales.pendientes}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500">Monto Pendiente</p>
                  <p className="text-2xl font-bold text-orange-600">{formatCurrency(gastosPropiedad.totales.monto_pendiente)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Actions */}
            {gastosPropiedad.totales.pendientes > 0 && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={selectAllPendientes}>
                  Seleccionar todos los pendientes
                </Button>
                <Button variant="outline" onClick={() => setSelectedGastos([])}>
                  Limpiar selección
                </Button>
              </div>
            )}

            {/* Gastos Table */}
            <Card>
              <CardHeader>
                <CardTitle>Detalle de Gastos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="w-10 py-3 px-4"></th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Fecha</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Categoría</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Descripción</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Monto</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Estado</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Comentario Cobro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gastosPropiedad.gastos.map((gasto, index) => (
                        <tr key={gasto.id} className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"} ${selectedGastos.includes(gasto.id) ? "bg-indigo-50" : ""}`}>
                          <td className="py-3 px-4">
                            {!gasto.cobrado && (
                              <input
                                type="checkbox"
                                checked={selectedGastos.includes(gasto.id)}
                                onChange={() => toggleGastoSelection(gasto.id)}
                                className="h-4 w-4 text-indigo-600 rounded"
                              />
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm">{formatDate(gasto.fecha_gasto)}</td>
                          <td className="py-3 px-4 text-sm text-gray-600">{gasto.categoria}</td>
                          <td className="py-3 px-4 text-sm">{gasto.descripcion}</td>
                          <td className="py-3 px-4 text-sm text-right font-medium">{formatCurrency(gasto.monto)}</td>
                          <td className="py-3 px-4 text-center">
                            {gasto.cobrado ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                <Check className="w-3 h-3 mr-1" />
                                Cobrado
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                                <Clock className="w-3 h-3 mr-1" />
                                Pendiente
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{gasto.comentario_cobro || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Tab Content: Otros Gastos - Por Categoría */}
        {activeTab === "otros" && !selectedCategoria && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Otros Gastos (No ligados a propiedades)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : otrosGastosPorCategoria.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p>No hay otros gastos registrados</p>
                  <p className="text-sm mt-2">Los gastos de oficina, personas o generales aparecerán aquí</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Categoría</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Total Gastos</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Monto Total</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Pendientes</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Monto Pendiente</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Estado</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {otrosGastosPorCategoria.map((cat, index) => (
                        <tr key={cat.categoria} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                          <td className="py-3 px-4 text-sm font-medium">{cat.categoria}</td>
                          <td className="py-3 px-4 text-sm text-center">{cat.total_gastos}</td>
                          <td className="py-3 px-4 text-sm text-right font-medium">{formatCurrency(cat.monto_total)}</td>
                          <td className="py-3 px-4 text-sm text-center">{cat.gastos_pendientes}</td>
                          <td className="py-3 px-4 text-sm text-right font-medium text-orange-600">{formatCurrency(cat.monto_pendiente)}</td>
                          <td className="py-3 px-4 text-center">
                            {cat.gastos_pendientes > 0 ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Pendiente
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Cobrado
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Button variant="ghost" size="sm" onClick={() => fetchGastosCategoria(cat.categoria)} title="Ver detalle">
                              <Eye className="w-4 h-4 mr-1" />
                              Ver
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Detalle de Otros Gastos por Categoría */}
        {activeTab === "otros" && selectedCategoria && gastosCategoria && (
          <>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => { setSelectedCategoria(null); setGastosCategoria(null); setSelectedGastos([]); }}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div className="flex-1">
                <h2 className="text-xl font-bold">Categoría: {gastosCategoria.categoria}</h2>
                <p className="text-gray-600">Gastos no ligados a propiedades</p>
              </div>
              {gastosCategoria.totales.pendientes > 0 && (
                <Button onClick={() => setShowCobrarModal(true)} data-testid="btn-cobrar-otros">
                  <Check className="w-4 h-4 mr-2" />
                  Marcar como Cobrados ({selectedGastos.length || "Seleccionar"})
                </Button>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500">Total Gastos</p>
                  <p className="text-2xl font-bold">{gastosCategoria.totales.cantidad}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500">Monto Total</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(gastosCategoria.totales.monto_total)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500">Pendientes</p>
                  <p className="text-2xl font-bold text-orange-600">{gastosCategoria.totales.pendientes}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500">Monto Pendiente</p>
                  <p className="text-2xl font-bold text-orange-600">{formatCurrency(gastosCategoria.totales.monto_pendiente)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Actions */}
            {gastosCategoria.totales.pendientes > 0 && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => {
                  const pendientes = gastosCategoria.gastos.filter(g => !g.cobrado).map(g => g.id);
                  setSelectedGastos(pendientes);
                }}>
                  Seleccionar todos los pendientes
                </Button>
                <Button variant="outline" onClick={() => setSelectedGastos([])}>
                  Limpiar selección
                </Button>
              </div>
            )}

            {/* Gastos Table */}
            <Card>
              <CardHeader>
                <CardTitle>Detalle de Gastos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="w-10 py-3 px-4"></th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Fecha</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Tipo</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Ligado a</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Descripción</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Monto</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Estado</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Comentario Cobro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gastosCategoria.gastos.map((gasto, index) => (
                        <tr key={gasto.id} className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"} ${selectedGastos.includes(gasto.id) ? "bg-indigo-50" : ""}`}>
                          <td className="py-3 px-4">
                            {!gasto.cobrado && (
                              <input
                                type="checkbox"
                                checked={selectedGastos.includes(gasto.id)}
                                onChange={() => toggleGastoSelection(gasto.id)}
                                className="h-4 w-4 text-indigo-600 rounded"
                              />
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm">{formatDate(gasto.fecha_gasto)}</td>
                          <td className="py-3 px-4 text-sm">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                              gasto.tipo_ligado === "Persona" ? "bg-purple-100 text-purple-800" :
                              gasto.tipo_ligado === "Oficina" ? "bg-yellow-100 text-yellow-800" :
                              "bg-gray-100 text-gray-800"
                            }`}>
                              {gasto.tipo_ligado || "General"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm font-medium">{getGastoLigadoDisplay(gasto)}</td>
                          <td className="py-3 px-4 text-sm">{gasto.descripcion}</td>
                          <td className="py-3 px-4 text-sm text-right font-medium">{formatCurrency(gasto.monto)}</td>
                          <td className="py-3 px-4 text-center">
                            {gasto.cobrado ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                <Check className="w-3 h-3 mr-1" />
                                Cobrado
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                                <Clock className="w-3 h-3 mr-1" />
                                Pendiente
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{gasto.comentario_cobro || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Modal: Nueva/Editar Caja Chica */}
        <Dialog open={showCajaModal} onOpenChange={setShowCajaModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCaja ? "Editar Caja Chica" : "Nueva Caja Chica"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="fecha_entrega">Fecha de Entrega *</Label>
                <Input
                  id="fecha_entrega"
                  type="date"
                  value={cajaForm.fecha_entrega}
                  onChange={(e) => setCajaForm({ ...cajaForm, fecha_entrega: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="monto_entregado">Monto Entregado *</Label>
                <Input
                  id="monto_entregado"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={cajaForm.monto_entregado}
                  onChange={(e) => setCajaForm({ ...cajaForm, monto_entregado: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="responsable">Responsable (Personal) *</Label>
                <Input
                  id="responsable"
                  placeholder="Nombre del personal de mantenimiento"
                  value={cajaForm.responsable}
                  onChange={(e) => setCajaForm({ ...cajaForm, responsable: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="origen">Origen del Dinero</Label>
                <Input
                  id="origen"
                  placeholder="Ej: Renta de la propiedad Las Lomas"
                  value={cajaForm.origen}
                  onChange={(e) => setCajaForm({ ...cajaForm, origen: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="descripcion">Descripción (opcional)</Label>
                <Textarea
                  id="descripcion"
                  placeholder="Notas adicionales..."
                  value={cajaForm.descripcion}
                  onChange={(e) => setCajaForm({ ...cajaForm, descripcion: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCajaModal(false); setEditingCaja(null); }}>
                Cancelar
              </Button>
              <Button onClick={handleSaveCaja}>
                {editingCaja ? "Guardar Cambios" : "Crear Caja"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Agregar Gasto */}
        <Dialog open={showGastoModal} onOpenChange={setShowGastoModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Agregar Gasto</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Tipo de Gasto *</Label>
                <Select 
                  value={gastoForm.tipo_ligado} 
                  onValueChange={(v) => setGastoForm({ ...gastoForm, tipo_ligado: v, contrato_id: "", persona_nombre: "" })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_LIGADO.map((tipo) => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {gastoForm.tipo_ligado === "Propiedad" && (
                <div>
                  <Label htmlFor="contrato_id">Propiedad *</Label>
                  <Select 
                    value={gastoForm.contrato_id} 
                    onValueChange={(v) => setGastoForm({ ...gastoForm, contrato_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar propiedad" />
                    </SelectTrigger>
                    <SelectContent>
                      {contratos.map((contrato) => (
                        <SelectItem key={contrato.id} value={contrato.id}>
                          {contrato.inmueble_nombre} - {contrato.propietario_nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {gastoForm.tipo_ligado === "Persona" && (
                <div>
                  <Label htmlFor="persona_nombre">Nombre de la Persona *</Label>
                  <Input
                    id="persona_nombre"
                    placeholder="Ej: Juan Pérez (Inquilino Depto 101)"
                    value={gastoForm.persona_nombre}
                    onChange={(e) => setGastoForm({ ...gastoForm, persona_nombre: e.target.value })}
                  />
                </div>
              )}

              <div>
                <Label htmlFor="fecha_gasto">Fecha del Gasto *</Label>
                <Input
                  id="fecha_gasto"
                  type="date"
                  value={gastoForm.fecha_gasto}
                  onChange={(e) => setGastoForm({ ...gastoForm, fecha_gasto: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="categoria">Categoría</Label>
                <Select 
                  value={gastoForm.categoria} 
                  onValueChange={(v) => setGastoForm({ ...gastoForm, categoria: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="descripcion">Descripción *</Label>
                <Input
                  id="descripcion"
                  placeholder="Descripción del gasto"
                  value={gastoForm.descripcion}
                  onChange={(e) => setGastoForm({ ...gastoForm, descripcion: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="monto">Monto *</Label>
                <Input
                  id="monto"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={gastoForm.monto}
                  onChange={(e) => setGastoForm({ ...gastoForm, monto: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGastoModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveGasto}>
                Agregar Gasto
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Marcar como Cobrados */}
        <Dialog open={showCobrarModal} onOpenChange={setShowCobrarModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Marcar Gastos como Cobrados</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                Se marcarán <span className="font-semibold">{selectedGastos.length}</span> gastos como cobrados.
              </p>
              <div>
                <Label htmlFor="comentario_cobro">Comentario del Cobro *</Label>
                <Textarea
                  id="comentario_cobro"
                  placeholder="Ej: Cobrado en el corte de Marzo 2026, factura #12345"
                  value={comentarioCobro}
                  onChange={(e) => setComentarioCobro(e.target.value)}
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">Indica el mes y corte en que se cobraron estos gastos</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCobrarModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleMarcarCobrados} disabled={selectedGastos.length === 0}>
                <Check className="w-4 h-4 mr-2" />
                Confirmar Cobro
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
